/// エンティティ Id のブランド型 Value Object (nominal な誤代入防止)。
///
/// 目的: `FirmId` を `LawyerId` が期待される箇所へ渡すといった取り違えを
/// コンパイル時に防ぐ (nominal typing)。値の中身は素の文字列だが、
/// `__brand` タグにより構造的に互換でなくなる。
///
/// バリデーションは UUID フォーマット検証のみ — backend の `define_id!` マクロ
/// (`apps/backend/crates/domain/src/core/ids.rs`) と思想を揃える。
/// backend が `Uuid::parse_str` で標準 36 文字ハイフン区切り UUID のみ受理するのに合わせ、
/// frontend も同形式に一致しない文字列 (空文字含む) は `null` を返す。
/// 妥当な場合は入力をそのまま保持 (verbatim、正規化なし)。
///
/// 対象フィールド:
///   - `shared/types/firm.ts:7` (id) と FK `firm_id`
///   - `shared/types/lawyer.ts:15` (id) と FK `lawyer_id`
///   - `shared/types/account.ts:46` (id) と FK `*_account_id`
///   - `shared/types/product.ts:47` (id) と FK `product_id`
///
/// issue #470 / 親 issue #430。

// UUID フォーマット判定は shared/util/uuid が単一ソース (domain → shared は許容方向)。
// readUuidArray など他の消費者と同じ意味論 (36 文字ハイフン区切り、version/variant 非依存) を共有する。
import { isUuid } from '@shared/util/uuid';

/** firms.id / FK firm_id の branded 型。 */
export type FirmId = string & { readonly __brand: 'FirmId' };

/** lawyers.id / FK lawyer_id の branded 型。 */
export type LawyerId = string & { readonly __brand: 'LawyerId' };

/** lawyer_emails.id の branded 型 (メール編集/削除 API のパスキー)。 */
export type LawyerEmailId = string & { readonly __brand: 'LawyerEmailId' };

/** companies.id / FK company_id の branded 型 (インハウス勤務先)。 */
export type CompanyId = string & { readonly __brand: 'CompanyId' };

/** app_accounts.id / FK *_account_id の branded 型。 */
export type AccountId = string & { readonly __brand: 'AccountId' };

/** products.id / FK product_id の branded 型。 */
export type ProductId = string & { readonly __brand: 'ProductId' };

/**
 * `value` を検証し、UUID 形式なら `FirmId` 型で返す。
 *
 * UUID 形式でない (空文字含む) 場合は `null` を返す (例外は投げない)。
 * 妥当な場合は入力をそのまま保持 (正規化なし)。
 */
export function createFirmId(value: string): FirmId | null {
  return isUuid(value) ? (value as FirmId) : null;
}

/**
 * `value` を検証し、UUID 形式なら `LawyerId` 型で返す。
 *
 * UUID 形式でない (空文字含む) 場合は `null` を返す (例外は投げない)。
 * 妥当な場合は入力をそのまま保持 (正規化なし)。
 */
export function createLawyerId(value: string): LawyerId | null {
  return isUuid(value) ? (value as LawyerId) : null;
}

/**
 * `value` を検証し、UUID 形式なら `LawyerEmailId` 型で返す。
 *
 * UUID 形式でない (空文字含む) 場合は `null` を返す (例外は投げない)。
 * 妥当な場合は入力をそのまま保持 (正規化なし)。
 */
export function createLawyerEmailId(value: string): LawyerEmailId | null {
  return isUuid(value) ? (value as LawyerEmailId) : null;
}

/**
 * `value` を検証し、UUID 形式なら `CompanyId` 型で返す。
 *
 * UUID 形式でない (空文字含む) 場合は `null` を返す (例外は投げない)。
 * 妥当な場合は入力をそのまま保持 (正規化なし)。
 */
export function createCompanyId(value: string): CompanyId | null {
  return isUuid(value) ? (value as CompanyId) : null;
}

/**
 * `value` を検証し、UUID 形式なら `AccountId` 型で返す。
 *
 * UUID 形式でない (空文字含む) 場合は `null` を返す (例外は投げない)。
 * 妥当な場合は入力をそのまま保持 (正規化なし)。
 */
export function createAccountId(value: string): AccountId | null {
  return isUuid(value) ? (value as AccountId) : null;
}

/**
 * `value` を検証し、UUID 形式なら `ProductId` 型で返す。
 *
 * UUID 形式でない (空文字含む) 場合は `null` を返す (例外は投げない)。
 * 妥当な場合は入力をそのまま保持 (正規化なし)。
 */
export function createProductId(value: string): ProductId | null {
  return isUuid(value) ? (value as ProductId) : null;
}
